/**
 * CanvasArea - Central canvas area with CSV uploader, JointJS canvas, status overlay, and loading
 */
import { RefObject } from 'react'
import { dia } from '@joint/core'
import { CSVUploader } from '../../../../../map-editor-features/csv'
import { StatusOverlay } from '../../../../../map-editor-features/status'
import { LoadingOverlay } from '../../../../../map-editor-shared/ui/LoadingOverlay'
import { useFloorStore } from '../../../../../map-editor-shared/store/floorStore'
import { EditorTool } from '../../../../../map-editor-shared/store/uiStore'
import { CanvasFloorViewer } from '../../../../../map-editor-features/viewer'
import { useGuidedMapStore } from '../../../../../map-editor-features/guided-map'
import { CanvasToolbar } from '../../../components/CanvasToolbar'
import { ObjectLabelToggleGroup } from '../../../components/ObjectLabelToggleGroup'
import { ObjectLabelToggles } from '../../../lib/objectLabelToggleStorage'
import type { ViewMode } from '../../../components/EditorHeader'
import type { EditorModalState } from '../types'
import styles from '../../../EditorPage.module.css'

interface CanvasAreaProps {
  canvasRef: RefObject<HTMLDivElement | null>
  mapId: string | undefined
  rawData: any
  parsedData: any
  currentFloor: string | null
  setModalState: React.Dispatch<React.SetStateAction<EditorModalState>>
  graph: dia.Graph | null
  paper: dia.Paper | null
  isRestoring: boolean
  isSaving: boolean
  currentTool: EditorTool
  onToolChange: (tool: EditorTool) => void
  canGroup?: boolean
  isGroupSelected?: boolean
  onGroup?: () => void
  onUngroup?: () => void
  onAutoGroupLights?: () => void
  labelToggles: ObjectLabelToggles
  onLabelTogglesChange: (next: ObjectLabelToggles) => void
  viewMode: ViewMode
}

export function CanvasArea({
  canvasRef,
  mapId,
  rawData,
  parsedData,
  currentFloor,
  setModalState,
  graph,
  paper,
  isRestoring,
  isSaving,
  currentTool,
  onToolChange,
  canGroup,
  isGroupSelected,
  onGroup,
  onUngroup,
  onAutoGroupLights,
  labelToggles,
  onLabelTogglesChange,
  viewMode,
}: CanvasAreaProps) {
  const floorData = currentFloor
    ? useFloorStore.getState().floors.find(f => f.id === currentFloor)
    : undefined
  const hasGraphData = (floorData?.mapData?.graphJson?.cells?.length ?? 0) > 0
  const hasLiveGraph = graph != null && graph.getCells().length > 0
  const isViewerMode = viewMode === 'viewer' && (hasGraphData || hasLiveGraph)
  // 가이드 진행 중에는 캔버스 위 편집 도구(툴바·미니맵·라벨 토글)를 숨긴다 —
  // 셋업 페이지에서 이어지는 위저드 화면이 에디터처럼 보이지 않게 유지.
  const guideActive = useGuidedMapStore(s => s.phase === 'editor')

  return (
    <div className={styles.canvasArea}>
      {/* Show CSV uploader only when no data exists at all (CSV or graph) */}
      {!rawData && !parsedData && !hasGraphData && !hasLiveGraph && (
        <CSVUploader
          key={mapId ?? 'no-map'}
          mapId={mapId}
          onMappingRequired={() => setModalState((prev) => ({ ...prev, mapping: true }))}
          currentFloor={currentFloor}
        />
      )}

      {/* 뷰어 모드 — 시뮬레이터·현장개요와 동일한 Canvas 렌더러(읽기전용, 휠 줌/드래그 팬).
          아래 JointJS 캔버스는 계속 살아있는 채로 CSS 로만 숨긴다(visibility) — 재초기화 없이
          에디터 모드로 즉시 되돌아갈 수 있게. */}
      {isViewerMode && floorData && (
        <div className={styles.viewerOverlay}>
          <CanvasFloorViewer floor={floorData} liveGraph={graph} />
        </div>
      )}

      {/* JointJS Canvas Container */}
      <div
        ref={canvasRef}
        className={styles.canvas}
        style={isViewerMode ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
      />

      {/* Status Overlay - Real-time status indicators for CCTV and Parking */}
      {!isViewerMode && graph && paper && (
        <StatusOverlay graph={graph} paper={paper} />
      )}


      {/* Canvas Toolbar - Figma-style interaction mode toggle */}
      {!isViewerMode && !guideActive && (
        <CanvasToolbar
          currentTool={currentTool}
          onToolChange={onToolChange}
          canGroup={canGroup}
          isGroupSelected={isGroupSelected}
          onGroup={onGroup}
          onUngroup={onUngroup}
          onAutoGroupLights={onAutoGroupLights}
        />
      )}

      {/* Object info label toggle group (top-left of canvas).
          Persists choice via localStorage; renders SVG labels under each object. */}
      {!isViewerMode && !guideActive && (
        <ObjectLabelToggleGroup
          value={labelToggles}
          onChange={onLabelTogglesChange}
        />
      )}

      {/* Loading / Saving Overlay */}
      {isSaving && <LoadingOverlay message="맵 저장중..." />}
      {isRestoring && !isSaving && <LoadingOverlay message="맵 로딩중..." />}
    </div>
  )
}
